import React, { useMemo } from 'react';
import './YearSummary.css';

const fmt = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const YearSummary = ({ transactions }) => {
  const yearData = useMemo(() => {
    const map = {};
    (transactions || []).forEach((t) => {
      const dateStr = t.date || t.data || '';
      const year = dateStr ? new Date(dateStr + 'T12:00:00').getFullYear() : null;
      if (!year || isNaN(year)) return;
      if (!map[year]) map[year] = { toReceive: 0, received: 0, toPay: 0, expenses: 0 };
      const value   = parseFloat(t.value || t.valor || 0);
      const type    = (t.type   || t.operacao || '').toLowerCase();
      const status  = (t.status || '').toLowerCase();
      const isIncome = ['receita', 'entrada'].includes(type);
      const isPaid   = ['pago', 'recebido'].includes(status);
      if (isIncome) { if (isPaid) map[year].received  += value; else map[year].toReceive += value; }
      else          { if (isPaid) map[year].expenses  += value; else map[year].toPay     += value; }
    });
    return Object.entries(map)
      .sort(([a], [b]) => Number(b) - Number(a))
      .map(([year, d]) => ({
        year,
        toReceive: d.toReceive,
        received:  d.received,
        toPay:     d.toPay,
        expenses:  d.expenses,
        projected: (d.received + d.toReceive) - (d.expenses + d.toPay),
        actual:    d.received - d.expenses,
      }));
  }, [transactions]);

  if (!yearData.length) return null;

  return (
    <div className="year-summary">
      <div className="year-summary-header">
        <span className="year-summary-icon">🏦</span>
        <h2 className="year-summary-title">Resumo de {new Date().getFullYear()}</h2>
      </div>

      {/* ── DESKTOP: tabela ── */}
      <div className="ys-table-wrap">
        <table className="year-summary-table">
          <thead>
            <tr>
              <th>ANO</th>
              <th>A RECEBER</th>
              <th>RECEBIDOS</th>
              <th>A PAGAR</th>
              <th>DESPESAS (PAGAS)</th>
              <th>SALDO PREVISTO</th>
              <th>SALDO REAL</th>
            </tr>
          </thead>
          <tbody>
            {yearData.map((row) => (
              <tr key={row.year}>
                <td className="ys-year">{row.year}</td>
                <td className="ys-to-receive">{fmt(row.toReceive)}</td>
                <td className="ys-received">{fmt(row.received)}</td>
                <td className="ys-to-pay">{fmt(row.toPay)}</td>
                <td className="ys-expenses">{fmt(row.expenses)}</td>
                <td className={`ys-projected ${row.projected >= 0 ? 'positive' : 'negative'}`}>{fmt(row.projected)}</td>
                <td className={`ys-actual    ${row.actual    >= 0 ? 'positive' : 'negative'}`}>{fmt(row.actual)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── MOBILE: cards por ano ── */}
      <div className="ys-cards">
        {yearData.map((row) => (
          <div key={row.year} className="ys-card">
            <div className="ys-card-grid">
              <div className="ys-card-item">
                <span className="ys-card-label">A Receber</span>
                <span className="ys-card-val to-receive">{fmt(row.toReceive)}</span>
              </div>
              <div className="ys-card-item">
                <span className="ys-card-label">Recebidos</span>
                <span className="ys-card-val received">{fmt(row.received)}</span>
              </div>
              <div className="ys-card-item">
                <span className="ys-card-label">A Pagar</span>
                <span className="ys-card-val to-pay">{fmt(row.toPay)}</span>
              </div>
              <div className="ys-card-item">
                <span className="ys-card-label">Despesas</span>
                <span className="ys-card-val expenses">{fmt(row.expenses)}</span>
              </div>
            </div>
            <div className="ys-card-footer">
              <div className="ys-card-saldo">
                <span className="ys-card-label">Saldo Previsto</span>
                <span className={`ys-card-val ${row.projected >= 0 ? 'positive' : 'negative'}`}>{fmt(row.projected)}</span>
              </div>
              <div className="ys-card-saldo">
                <span className="ys-card-label">Saldo Real</span>
                <span className={`ys-card-val ${row.actual >= 0 ? 'positive' : 'negative'}`}>{fmt(row.actual)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default YearSummary;
